import { useState, useEffect, useRef } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useDataCache } from '../context/DataCacheContext';
import axios from 'axios';
import { cachedGet, invalidateCache } from '../utils/apiCache';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import AppLayout from '../components/AppLayout';
import { toast } from 'sonner';
import { track, EVENTS } from '../utils/analytics';
import { 
  FileText, 
  Upload,
  Trash2,
  File,
  X,
  CheckCircle2,
  Loader2,
  Pencil
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
// Same-origin API — bypasses CORS and Cloudflare on custom domains
const SAME_ORIGIN_API = `${typeof window !== 'undefined' ? window.location.origin : ''}/api`;

// Toggle debug logging (set to false for production)
const DEBUG_UPLOAD = process.env.NODE_ENV !== 'production';
const log = (...args) => DEBUG_UPLOAD && console.log(...args);
const warn = (...args) => DEBUG_UPLOAD && console.warn(...args);


const CVManager = () => {
  const [cvs, setCvs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [uploadConsent, setUploadConsent] = useState(false);
  const fileInputRef = useRef(null);
  const { getAuthHeaders, token } = useAuth();
  const dataCache = useDataCache();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isOnboarding = searchParams.get('onboarding') === 'true';

  const addDebug = () => {};

  useEffect(() => {
    fetchCVs();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchCVs = async () => {
    try {
      const { data } = await cachedGet(`${API}/cv/list`, { headers: getAuthHeaders() }, 60 * 1000, (fresh) => setCvs(fresh));
      setCvs(data);
    } catch (error) {
      console.error('Failed to fetch CVs:', error);
      toast.error('Failed to load CVs');
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async (file) => {
    if (!file) return;

    // Validation
    const validTypes = ['.pdf', '.docx', '.txt'];
    const fileExt = '.' + file.name.split('.').pop().toLowerCase();
    if (!validTypes.includes(fileExt)) {
      toast.error('Unsupported file type. Please upload PDF, DOCX, or TXT.');
      return;
    }

    if (file.size > 50 * 1024 * 1024) {
      toast.error('File too large. Maximum size is 50MB.');
      return;
    }

    setUploading(true);
    setUploadProgress(0);
    track(EVENTS.CV_UPLOAD_STARTED, { filename: file.name, size_kb: Math.round(file.size / 1024) });

    const fileSizeKB = Math.round(file.size / 1024);
    addDebug(`FILE: ${file.name} (${fileSizeKB}KB, ${file.type || 'unknown type'})`);
    addDebug(`ORIGIN: ${window.location.origin}`);
    addDebug(`AUTH: token ${token ? 'present' : 'MISSING!'}`);

    const authHeader = getAuthHeaders().Authorization;
    let result = null;

    try {
      // METHOD 1: Same-origin FormData (bypasses CORS + Cloudflare entirely)
      try {
        addDebug(`[1] Same-origin multipart → ${SAME_ORIGIN_API}/cv/upload`);
        const formData = new FormData();
        formData.append('file', file);
        const res = await axios.post(`${SAME_ORIGIN_API}/cv/upload`, formData, {
          headers: { Authorization: authHeader },
          timeout: 60000,
          onUploadProgress: (e) => setUploadProgress(Math.min(Math.round((e.loaded * 100) / (e.total || file.size)), 95)),
        });
        addDebug(`SUCCESS! Status: ${res.status}`);
        setUploadProgress(100);
        result = res.data;
      } catch (err1) {
        addDebug(`[1] Failed: ${err1?.response?.status || err1?.code || err1?.message}`);

        // METHOD 2: Cross-origin FormData (in case same-origin routing isn't set up)
        try {
          addDebug(`[2] Cross-origin multipart → ${API}/cv/upload`);
          const formData2 = new FormData();
          formData2.append('file', file);
          const res = await axios.post(`${API}/cv/upload`, formData2, {
            headers: { Authorization: authHeader },
            timeout: 60000,
            onUploadProgress: (e) => setUploadProgress(Math.min(Math.round((e.loaded * 100) / (e.total || file.size)), 95)),
          });
          addDebug(`SUCCESS! Status: ${res.status}`);
          setUploadProgress(100);
          result = res.data;
        } catch (err2) {
          addDebug(`[2] Failed: ${err2?.response?.status || err2?.code || err2?.message}`);

          // METHOD 3: Read file → base64 JSON (same-origin)
          let base64;
          try {
            addDebug('[3] Reading file for base64 upload...');
            const buffer = await file.arrayBuffer();
            const bytes = new Uint8Array(buffer);
            let binary = '';
            for (let i = 0; i < bytes.length; i += 8192) {
              binary += String.fromCharCode(...bytes.subarray(i, i + 8192));
            }
            base64 = btoa(binary);
            addDebug(`File read OK (${base64.length} chars)`);
          } catch (readErr) {
            addDebug(`[3] File read failed: ${readErr.message}`);

            // METHOD 4: FileReader as last resort
            try {
              addDebug('[4] Trying FileReader...');
              base64 = await new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.onload = () => resolve(reader.result.split(',')[1]);
                reader.onerror = () => reject(reader.error);
                reader.readAsDataURL(file);
              });
              addDebug(`FileReader OK (${base64.length} chars)`);
            } catch (frErr) {
              addDebug(`[4] FileReader failed: ${frErr?.message || frErr}`);
              throw new Error('This file is stored in iCloud and hasn\'t been downloaded to your Mac. In Finder, right-click the file → "Download Now", then try uploading again. Or drag it to your Desktop first.');
            }
          }

          // Send base64 via same-origin, then cross-origin
          const jsonHeaders = { Authorization: authHeader, 'Content-Type': 'application/json' };
          const payload = { filename: file.name, data: base64 };

          try {
            addDebug(`[3b] base64 JSON → ${SAME_ORIGIN_API}/cv/upload-base64`);
            const res = await axios.post(`${SAME_ORIGIN_API}/cv/upload-base64`, payload, { headers: jsonHeaders, timeout: 60000 });
            addDebug(`SUCCESS! Status: ${res.status}`);
            setUploadProgress(100);
            result = res.data;
          } catch (err3) {
            addDebug(`[3b] Failed: ${err3?.response?.status || err3?.code || err3?.message}`);
            addDebug(`[3c] base64 JSON → ${API}/cv/upload-base64`);
            const res = await axios.post(`${API}/cv/upload-base64`, payload, { headers: jsonHeaders, timeout: 60000 });
            addDebug(`SUCCESS! Status: ${res.status}`);
            setUploadProgress(100);
            result = res.data;
          }
        }
      }

      if (result) {
        setCvs([result, ...cvs]);
        invalidateCache('/cv/list');
        invalidateCache('/jobs/feed-scored');
        setUploadSuccess(true);
        toast.success('CV uploaded successfully!');
        track(EVENTS.CV_UPLOAD_COMPLETED, { cv_id: result?.id });
        dataCache?.invalidate('jobfeed');
        setTimeout(() => setUploadSuccess(false), 3000);
      }
    } catch (error) {
      console.error('[CV Upload] ALL METHODS FAILED:', error);

      const status = error?.response?.status;
      const detail = error?.response?.data?.detail;
      const code = error?.code;

      addDebug(`ALL METHODS FAILED: ${status || code || 'unknown'}`);
      addDebug(`${detail || error?.message || 'No details available'}`);

      let msg;
      if (error?.message?.includes('Cannot read') || error?.message?.includes('iCloud') || error?.message?.includes('Download')) {
        msg = error.message;
      } else if (!navigator.onLine || code === 'ERR_NETWORK') {
        msg = 'Network error. Check your internet connection.';
      } else if (status === 401) {
        msg = 'Session expired. Please log out and log in again.';
      } else if (status === 413) {
        msg = 'File too large for the server.';
      } else if (detail) {
        msg = detail;
      } else {
        msg = 'Upload failed. See the log below for details.';
      }
      toast.error(msg);
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const handleDelete = async (cvId) => {
    if (!window.confirm('Are you sure you want to delete this CV?')) return;
    try {
      await axios.delete(`${API}/cv/${cvId}`, { headers: getAuthHeaders() });
      setCvs(cvs.filter(cv => cv.id !== cvId));
      toast.success('CV deleted');
    } catch (error) {
      toast.error('Failed to delete CV');
    }
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (!uploadConsent) {
      toast.error('Please check the consent box before uploading.');
      return;
    }
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleUpload(e.dataTransfer.files[0]);
    }
  };

  const handleFileInput = (e) => {
    if (!uploadConsent) {
      toast.error('Please check the consent box before uploading.');
      return;
    }
    if (e.target.files && e.target.files[0]) {
      handleUpload(e.target.files[0]);
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <AppLayout>
      <div data-testid="cv-manager-page">
          {/* Onboarding Banner */}
          {isOnboarding && cvs.length === 0 && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-6 bg-[#0a0a0a] border border-white/[0.06] rounded-2xl p-6 text-white"
              data-testid="onboarding-banner"
            >
              <h2 className="font-heading text-2xl font-bold mb-2">Welcome to JobSurf AI!</h2>
              <p className="text-zinc-400 mb-1">Upload your CV below to get started. Our AI will parse your skills and experience to:</p>
              <ul className="text-zinc-400 text-sm mt-2 space-y-1 ml-4 list-disc">
                <li>Match you with the best job opportunities</li>
                <li>Generate tailored cover letters</li>
                <li>Auto-fill job applications</li>
              </ul>
            </motion.div>
          )}

          {/* Header */}
          <div className="mb-6 sm:mb-8">
            <h1 className="font-heading text-2xl sm:text-3xl font-bold text-zinc-900 dark:text-white mb-1 sm:mb-2">My CVs</h1>
            <p className="text-sm sm:text-base text-zinc-600 dark:text-zinc-400">Upload and manage your resumes for job applications</p>
          </div>

          {/* Upload Area */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className={`mb-6 sm:mb-8 border-2 border-dashed rounded-2xl p-5 sm:p-8 text-center transition-all duration-200 ${
              dragActive 
                ? 'border-cyan-500 bg-cyan-500/10 dark:bg-cyan-500/10' 
                : 'border-zinc-200 dark:border-white/[0.12] bg-white dark:bg-[#0a0a0a] hover:border-zinc-300 dark:hover:border-white/[0.2] hover:bg-zinc-50 dark:hover:bg-[#0e0e0e]'
            }`}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf,.docx,.txt"
              onChange={handleFileInput}
              className="hidden"
              data-testid="cv-file-input"
            />
            
            {uploading ? (
              <div className="py-8" data-testid="upload-progress-area">
                <Loader2 className="w-10 h-10 mx-auto mb-4 text-cyan-500 animate-spin" />
                <p className="text-zinc-700 dark:text-zinc-300 font-medium mb-3">
                  {uploadProgress < 100 ? 'Uploading your CV...' : 'Processing...'}
                </p>
                <div className="w-64 mx-auto bg-zinc-200 dark:bg-zinc-700 rounded-full h-2.5 overflow-hidden">
                  <div
                    className="h-full bg-cyan-500 rounded-full transition-all duration-300 ease-out"
                    style={{ width: `${uploadProgress}%` }}
                    data-testid="upload-progress-bar"
                  />
                </div>
                <p className="text-sm text-zinc-500 mt-2" data-testid="upload-progress-text">{uploadProgress}%</p>
              </div>
            ) : uploadSuccess ? (
              <div className="py-8" data-testid="upload-success-area">
                <CheckCircle2 className="w-12 h-12 mx-auto mb-4 text-teal-500" />
                <h3 className="font-heading font-semibold text-lg text-teal-700 mb-2">Upload Complete!</h3>
                <p className="text-zinc-500 dark:text-zinc-400">Your CV is being parsed by AI...</p>
              </div>
            ) : (
              <>
                <Upload className={`w-10 h-10 sm:w-12 sm:h-12 mx-auto mb-3 sm:mb-4 ${dragActive ? 'text-cyan-500' : 'text-zinc-500'}`} />
                <h3 className="font-heading font-semibold text-base sm:text-lg text-zinc-900 dark:text-white mb-1 sm:mb-2">
                  {dragActive ? 'Drop your CV here' : 'Upload your CV'}
                </h3>
                <p className="text-sm text-zinc-500 mb-3 sm:mb-4">Drag and drop or click to browse</p>
                <Button
                  onClick={() => fileInputRef.current?.click()}
                  className="bg-cyan-600 hover:bg-cyan-500 text-white rounded-full disabled:opacity-40 disabled:cursor-not-allowed"
                  disabled={!uploadConsent}
                  data-testid="upload-cv-btn"
                >
                  Choose File
                </Button>
                <p className="text-xs text-zinc-400 mt-4">Supported: PDF, DOCX, TXT (max 50MB)</p>
                <div className="mt-5 max-w-md mx-auto text-left">
                  <label
                    className={`flex items-start gap-3 cursor-pointer rounded-xl border-2 px-4 py-3 transition-all ${
                      uploadConsent
                        ? 'border-cyan-500/70 bg-cyan-500/10'
                        : 'border-amber-500/60 bg-amber-500/5 hover:border-amber-400/80'
                    }`}
                    data-testid="cv-upload-consent"
                  >
                    <input
                      type="checkbox"
                      checked={uploadConsent}
                      onChange={(e) => setUploadConsent(e.target.checked)}
                      className="mt-1 h-5 w-5 shrink-0 rounded border-2 border-amber-400 bg-zinc-900 text-cyan-500 focus:ring-2 focus:ring-cyan-500 cursor-pointer accent-cyan-500"
                      data-testid="cv-upload-consent-checkbox"
                    />
                    <span className="text-xs text-zinc-300 leading-relaxed">
                      <strong className="text-amber-400 block mb-1">Required: Tap to agree</strong>
                      I authorize JobSurf AI to process my CV and profile data using AI to generate application materials and, where enabled by me, assist in submitting job applications on my behalf. I understand that JobSurf AI does not guarantee interviews, offers, or employment, and that I am responsible for reviewing all materials for accuracy. <Link to="/privacy" className="text-indigo-400 hover:underline">Privacy Policy</Link> &middot; <Link to="/terms" className="text-indigo-400 hover:underline">Terms</Link>
                    </span>
                  </label>
                </div>
              </>
            )}
          </motion.div>

          {/* CV List */}
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="w-8 h-8 border-4 border-cyan-500 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : cvs.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="w-16 h-16 mx-auto mb-4 text-zinc-300" />
              <p className="text-zinc-500 text-lg">No CVs uploaded yet</p>
              <p className="text-zinc-400 text-sm mt-1">Upload your first CV to start applying to jobs</p>
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
              {cvs.map((cv, index) => (
                <motion.div
                  key={cv.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-white dark:bg-[#0a0a0a] rounded-2xl border border-zinc-100 dark:border-white/[0.06] p-4 sm:p-6 shadow-soft hover:shadow-hover transition-all duration-300"
                >
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 sm:w-12 sm:h-12 bg-indigo-100 dark:bg-white/[0.06] dark:border dark:border-white/[0.08] rounded-xl flex items-center justify-center flex-shrink-0">
                      <File className="w-5 h-5 sm:w-6 sm:h-6 text-indigo-600 dark:text-cyan-400" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-medium text-sm sm:text-base text-zinc-900 dark:text-white leading-snug line-clamp-2" title={cv.filename}>{cv.filename}</h3>
                      <p className="text-xs sm:text-sm text-zinc-500 dark:text-zinc-400 mt-0.5">{formatDate(cv.uploaded_at)}</p>
                    </div>
                  </div>

                  {cv.parsed_content && (
                    <div className="mt-3 sm:mt-4 p-2.5 sm:p-3 bg-zinc-50 dark:bg-white/[0.04] rounded-xl">
                      <div className="flex items-center gap-1.5 text-xs sm:text-sm text-teal-600 mb-2">
                        <CheckCircle2 className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                        <span>Parsed successfully</span>
                      </div>
                      <div className="flex flex-wrap gap-1">
                        {cv.parsed_content.skills?.slice(0, 3).map((skill, i) => (
                          <span key={i} className="px-1.5 sm:px-2 py-0.5 bg-white dark:bg-white/[0.06] rounded text-[10px] sm:text-xs text-zinc-600 dark:text-zinc-400 border border-zinc-100 dark:border-white/[0.08] leading-tight">
                            {skill}
                          </span>
                        ))}
                        {cv.parsed_content.skills?.length > 3 && (
                          <span className="px-1.5 sm:px-2 py-0.5 bg-indigo-100 dark:bg-cyan-500/10 rounded text-[10px] sm:text-xs text-indigo-600 dark:text-cyan-400 leading-tight">
                            +{cv.parsed_content.skills.length - 3} more
                          </span>
                        )}
                      </div>
                    </div>
                  )}

                  <div className="mt-3 sm:mt-4 flex gap-2">
                    <Button
                      onClick={() => navigate(`/cv/edit/${cv.id}`)}
                      className="flex-1 rounded-xl bg-cyan-600 hover:bg-cyan-500 text-white text-sm"
                      data-testid={`edit-cv-${cv.id}`}
                    >
                      <Pencil className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1.5 sm:mr-2" />
                      Edit & Tailor
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => handleDelete(cv.id)}
                      className="rounded-xl border-zinc-200 dark:border-white/[0.08] text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-500/10 hover:border-red-200 dark:hover:border-red-500/20 px-3"
                      data-testid={`delete-cv-${cv.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
      </div>
    </AppLayout>
  );
};

export default CVManager;
